import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { SessionService } from '../../../core/services/session.service';
import { WorkspaceMobileBrandComponent } from '../../../shared/workspace/workspace-mobile-brand.component';
import { WorkspaceSidebarBrandComponent } from '../../../shared/workspace/workspace-sidebar-brand.component';
import { WorkspaceIconsModule } from '../components/workspace-icons.module';

type NavItem = { path: string; label: string; icon: string };
type NavGroup = { id: string; title: string; items: NavItem[]; separatorBefore?: boolean };

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    WorkspaceIconsModule,
    WorkspaceMobileBrandComponent,
    WorkspaceSidebarBrandComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrls: [
    './shell.component.css',
    '../../../shared/workspace/workspace-brand.css',
    '../../../shared/workspace/workspace-shell-mobile.css',
  ],
})
export class ShellComponent {
  readonly session = inject(SessionService);
  private readonly router = inject(Router);
  readonly navOpen = signal(false);
  readonly expandedGroup = signal<string | null>(null);

  readonly navGroups = computed((): NavGroup[] => {
    const base = '/enforcement';
    const groups: NavGroup[] = [
      {
        id: 'dashboard',
        title: '📊 Dashboard',
        items: [{ path: `${base}/dashboard`, label: 'Overview', icon: 'layout-dashboard' }],
      },
    ];

    const setupItems: NavItem[] = [];
    if (this.session.canAccessScans()) {
      setupItems.push({ path: `${base}/areas`, label: 'Locations', icon: 'map-pin' });
    }
    if (this.session.canAccessConfig()) {
      setupItems.push({ path: `${base}/plot-boundaries`, label: 'Plot boundaries', icon: 'map' });
    }
    if (setupItems.length) {
      groups.push({ id: 'setup', title: '🗂️ Setup', items: setupItems });
    }

    const surveyItems: NavItem[] = [];
    if (this.session.canAccessPlans()) {
      surveyItems.push({ path: `${base}/plans`, label: 'Sanctioned plans', icon: 'file-text' });
    }
    if (this.session.canAccessScans()) {
      surveyItems.push({ path: `${base}/lidar-survey`, label: 'LiDAR Survey', icon: 'scan-line' });
    }
    if (surveyItems.length) {
      groups.push({ id: 'survey', title: '📥 Survey & Plans', items: surveyItems });
    }

    const complianceItems: NavItem[] = [];
    if (this.session.canAccessMeasurements()) {
      complianceItems.push({ path: `${base}/measurements`, label: 'Measurements', icon: 'ruler' });
      complianceItems.push({
        path: `${base}/compliance-runs`,
        label: 'Rule-check history',
        icon: 'clipboard-check',
      });
    }
    if (this.session.canAccessFlags()) {
      complianceItems.push({ path: `${base}/flags`, label: 'Violation flags', icon: 'flag' });
    }
    if (complianceItems.length) {
      groups.push({ id: 'compliance', title: '✅ Compliance', items: complianceItems });
    }

    if (this.session.canAccessFlags() || this.session.canAccessConfig()) {
      groups.push({
        id: 'audit',
        title: '🔍 Audit',
        items: [{ path: `${base}/audit`, label: 'Audit trail', icon: 'scroll-text' }],
      });
    }

    const adminItems: NavItem[] = [];
    if (this.session.canAccessConfig()) {
      adminItems.push({ path: `${base}/location-types`, label: 'Location types', icon: 'layers' });
    }
    if (this.session.canAccessByeLaws()) {
      adminItems.push({ path: `${base}/bye-laws`, label: 'Bye-law norms', icon: 'book-open' });
    }
    if (this.session.canAccessConfig()) {
      adminItems.push({ path: `${base}/administration`, label: 'General settings', icon: 'settings' });
    }
    if (adminItems.length) {
      groups.push({
        id: 'administration',
        title: '⚙️ Administration',
        items: adminItems,
      });
    }

    return groups;
  });

  constructor() {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.navOpen.set(false);
    });

    effect((onCleanup) => {
      const lock = this.navOpen();
      document.body.style.overflow = lock ? 'hidden' : '';
      onCleanup(() => {
        document.body.style.overflow = '';
      });
    });
  }

  isGroupExpanded(id: string): boolean {
    return this.expandedGroup() === id;
  }

  toggleGroup(id: string): void {
    this.expandedGroup.update((current) => (current === id ? null : id));
  }

  toggleNav(): void {
    this.navOpen.update((open) => !open);
  }

  closeNav(): void {
    this.navOpen.set(false);
  }

  logout(): void {
    this.session.logout();
    void this.router.navigateByUrl('/');
  }
}
